import React from 'react';

/**
 * SeoHead — declarative SEO meta component.
 *
 * React 19 hoists any <title>, <meta>, <link> rendered inside a component
 * tree up to the document <head>. This lets each public page declare its
 * own SEO metadata inline without needing react-helmet.
 *
 * When the prerender script (scripts/prerender.js) crawls the build with
 * headless Chromium, those hoisted tags are baked into the saved HTML.
 * That means Google, Bing, LinkedIn preview bots, Slack unfurls, etc.
 * all see the correct <title> and Open Graph tags before any JS runs.
 *
 * Usage:
 *   <SeoHead
 *     title="Pricing — Blayzrr"
 *     description="Simple, transparent pricing..."
 *     path="/pricing"
 *   />
 */

const SITE = 'https://blayzrr.com';
const DEFAULT_OG_IMAGE = `${SITE}/landing-shots/hero.png`;

export default function SeoHead({
  title,
  description,
  path = '/',
  ogImage,
  ogType = 'website',
  robots,
  keywords,
}) {
  const url = `${SITE}${path.startsWith('/') ? path : `/${path}`}`;
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {robots && <meta name="robots" content={robots} />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Blayzrr" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
    </>
  );
}
